// @ts-strict
import { QueryKey, useInfiniteQuery, useQuery, useMutation } from "@tanstack/react-query";
import jsonStableStringify from "json-stable-stringify";
import { isEmpty } from "lodash-es";
import { createContext, useContext, useEffect, useState } from "react";
import { useSelector, useDispatch as useUntypedDispatch } from "react-redux";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import * as R from "remeda";

import { useAppState } from "@/context/app-state";
import { adminFeatureFlags, futureFeatureFlags } from "@/hooks/use-flag";
import { logOutUser } from "@/redux/auth/sessions/actions";
import { getAppList } from "@/redux/data/main/apps/actions";
import { getTeam, listTeamEntitlements } from "@/redux/data/main/teams/actions";
import { getKotsAppList } from "@/redux/kots/app/actions";
import { getKotsAppChannels } from "@/redux/kots/channels/actions";
import {
  getActiveKotsReleases,
  getKotsReleaseList
} from "@/redux/kots/releases/actions";
import {
  Channel,
  CustomerImpactCVEDetail,
  CustomerImpactCVEReleaseCustomersResponse,
  CustomerImpactCVEsResponse,
  CustomerImpactResponse,
  CustomerSearchResponse,
  DataLoading,
  FeatureValue,
  FeaturesResponse,
  KotsApp,
  KotsRelease,
  ReleaseExposure,
  KotsReleasesResponse,
  PlatformApp,
  Team,
  TeamEntitlements,
  SBOMResponse,
  ScanResponse,
  ImageReleaseSearchResponse
} from "@/types";
import { apiFetchVendorV1, apiFetchVendor } from "@/utilities/VendorUtilities";
import { useQueryParam } from "@/hooks/useQueryParamHelpers";

export const useDispatch = (): ThunkDispatch<any, any, AnyAction> => {
  return useUntypedDispatch();
};

type SetQuery = <QK>(params: {
  stableKey: string;
  queryFn: (params: { queryKey: QK }) => Promise<any>;
  enabled: boolean;
  networkMode: "always" | "online";
}) => void;

interface ReduxQueryContextValue {
  setQuery: SetQuery;
  refetch: (stableKey: string) => void;
}

const ReduxQueryContext = createContext<ReduxQueryContextValue>(
  {} as ReduxQueryContextValue
);

interface Queries {
  [key: string]: {
    queryFn: any;
    lastRunAt: number;
    networkMode: "always" | "online";
  };
}

export const ReduxQueryProvider = ({ children }: { children: React.ReactNode }) => {
  const [queue, setQueue] = useState(() => [] as string[]);
  const [queries, setQueries] = useState<Queries>({} as Queries);

  useEffect(() => {
    if (!queue.length) {
      return;
    }
    queue.forEach(key => {
      const queryObj = queries[key];
      if (!queryObj) {
        return;
      }
      const { queryFn, lastRunAt, networkMode } = queryObj;
      const now = Date.now();
      // don't run more than once every 30 seconds
      // call refetch after mutations
      if (networkMode !== "always" && now - lastRunAt < 1000 * 30) {
        return;
      }
      queryFn({ queryKey: JSON.parse(key) });
      setQueries(queries => ({
        ...queries,
        [key]: { ...queries[key], lastRunAt: now }
      }));
    });
    setQueue([]);
  }, [queue]);

  const setQuery: SetQuery = ({ stableKey, queryFn, enabled, networkMode }) => {
    setQueries(state => {
      if (state[stableKey]) {
        return state;
      }
      return { ...state, [stableKey]: { queryFn, lastRunAt: 0, networkMode } };
    });
    setQueue(state => {
      if (
        (enabled && state.includes(stableKey)) ||
        (!enabled && !state.includes(stableKey))
      ) {
        return state;
      }
      return enabled
        ? R.uniq([...state, stableKey])
        : R.reject(state, key => key === stableKey);
    });
  };

  const refetch = (stableKey: string) => {
    setQueries(state => ({
      ...state,
      [stableKey]: { ...state[stableKey], lastRunAt: 0 }
    }));
    setQueue(state => R.uniq([...state, stableKey]));
  };

  const contextValue = {
    setQuery,
    refetch
  };

  return (
    <ReduxQueryContext.Provider value={contextValue}>
      {children}
    </ReduxQueryContext.Provider>
  );
};

interface UseReduxQueryParams<QK> {
  queryKey: QK;
  queryFn: (params: { queryKey: QK }) => Promise<any>;
  enabled?: boolean;
  networkMode?: "always" | "online";
}

const useReduxQuery = <QK extends QueryKey>({
  queryKey,
  queryFn,
  enabled = true,
  // "always" always runs `queryFn`, "online" only runs once per queryKey per 30 seconds.
  // React Query defaults to "online", but our redux store doesn't store values keyed by
  // params - each dispatch generally overwrites the last, so "always" is safer. Only use
  // "online" for queries that are consistent for the entire team, like `teams`, `apps`,
  // and `teamEntitlements`. If a query is causing unexpected referesh/re-renders, see if
  // it's possible to use "online". Only these two modes are supported.
  //
  // In case of a mutation that would change the result of a query, call `refetch`.
  networkMode = "always"
}: UseReduxQueryParams<QK>) => {
  const { isAuthenticated } = useAppState();
  const { setQuery, refetch } = useContext(ReduxQueryContext);
  const stableKey = jsonStableStringify(queryKey);

  useEffect(() => {
    if (stableKey) {
      setQuery<QK>({
        stableKey,
        queryFn,
        enabled: isAuthenticated && enabled,
        networkMode
      });
    }
  }, [stableKey, enabled, isAuthenticated]);
  return {
    refetch: () => stableKey && refetch(stableKey)
  };
};

/**
 * A hook that retrieves a list of KOTS apps for the current user's team.
 *
 * @example
 * const kotsApps = useKotsApps();
 * const app = kotsApps?.find(app => app.id === appId);
 */
export const useKotsApps = (excludeChannels = true) => {
  const dispatch = useDispatch();
  useReduxQuery({
    queryKey: ["kotsApps", excludeChannels],
    queryFn: () => dispatch(getKotsAppList(excludeChannels)),
    networkMode: "online"
  });
  return useSelector<any, KotsApp[]>((state: any) => state.kots.apps.kotsApps);
};

/**
 * A react-query custom hook that retrieves the current user's team.
 */
export const useTeam = () => {
  const dispatch = useDispatch();
  useReduxQuery({
    queryKey: ["team"],
    queryFn: () => dispatch(getTeam()),
    networkMode: "online"
  });
  return useSelector<any, Team>(
    (state: any) => state.data.main.teamsAndTokens.teamsData.team
  );
};

export const useTeamEntitlementsQuery = () => {
  const dispatch = useDispatch();
  const { refetch } = useReduxQuery({
    queryKey: ["teamEntitlements"],
    queryFn: () => dispatch(listTeamEntitlements()),
    networkMode: "online"
  });
  const data = useSelector<any, TeamEntitlements>(
    (state: any) => state.data.main.teamsAndTokens.teamsData.teamEntitlements
  );
  const isLoading = useSelector<any, DataLoading>(
    (state: any) => state.ui.main.loading
  ).listTeamEntitlementsLoading;
  const isSuccess = !isEmpty(data);
  return { data, isLoading, isSuccess, refetch };
};

/**
 * A react-query custom hook that retrieves the current user's team entitlements.
 */
export const useTeamEntitlements = () => {
  const { data } = useTeamEntitlementsQuery();
  return data;
};

/**
 * A react-query custom hook that retrieves Platform apps for the current user's team.
 *
 * @returns object with keys:
 * - `platformApps`: an object of Platform apps for the current user's team, keyed by id
 * - `platformAppsList`: a list of id's of the Platform apps for the current user's team
 */
export const usePlatformAppsQuery = (enabled = true) => {
  const dispatch = useDispatch();
  useReduxQuery({
    queryKey: ["platformApps"],
    queryFn: () => dispatch(getAppList()),
    networkMode: "online",
    enabled
  });
  const platformApps = useSelector<any, { [id: string]: PlatformApp }>(
    (state: any) => state.data.main.apps.apps
  );
  const platformAppsList = useSelector<any, string[]>(
    (state: any) => state.ui.main.apps.appList
  );
  const platformAppsLoaded = useSelector<any, boolean>(
    (state: any) => state.data.main.apps.appsLoaded.platform
  );
  const data = { platformApps, platformAppsList };
  return { data, isSuccess: platformAppsLoaded };
};

/**
 * A react-query custom hook that retrieves the channels for an app.
 * @param excludeDetail - whether to exclude additional details in the response
 * @param excludeAdoption - whether to exclude the adoption field in the response
 * @param startIndex - the index to start at for pagination
 * @param pageCount - how many releases to fetch for pagination
 * @param channelNameFilter - the channel name filter to apply
 * @returns result partial {@link UseQueryResult}
 * @example
 * const { data } = useAppChannels(excludeDetail, excludeAdoption, startIndex, pageCount, channelNameFilter);
 */
export const useAppChannels = (
  excludeDetail?: boolean,
  excludeAdoption?: boolean,
  startIndex?: number,
  pageCount?: number,
  channelNameFilter?: string,
  includeSecuritySummary?: boolean
) => {
  const { currentAppId } = useAppState();
  const dispatch = useDispatch();
  const { refetch } = useReduxQuery({
    queryKey: [
      "kotsAppChannels",
      currentAppId,
      excludeDetail,
      excludeAdoption,
      startIndex,
      pageCount,
      channelNameFilter,
      includeSecuritySummary
    ] as const,
    queryFn: ({ queryKey }) =>
      dispatch(
        getKotsAppChannels(
          queryKey[1],
          queryKey[2],
          queryKey[3],
          queryKey[4],
          queryKey[5],
          queryKey[6],
          queryKey[7]
        )
      ),
    enabled: !!currentAppId
  });

  const appChannels = useSelector<any, Channel[]>(
    (state: any) => state.kots.channels.appChannels
  );
  const enterpriseChannels = useSelector<any, Channel[]>(
    (state: any) => state.kots.channels.enterpriseChannels
  );
  const totalCount = useSelector<any, number>(
    (state: any) => state.kots.channels.totalCount
  );
  const isLoading = useSelector<any, DataLoading>(
    (state: any) => state.ui.main.loading
  ).kotsAppChannelsLoading;

  return {
    appChannels,
    enterpriseChannels,
    totalCount,
    isLoading,
    refetch
  };
};

/**
 * A react-query custom hook that logs the current user out.
 */
export const useLogOutUserMutation = () => {
  const dispatch = useDispatch();
  const mutate = async (removeNextLink: boolean) => {
    try {
      dispatch(logOutUser(removeNextLink));
    } catch (error) {
      return;
    }
  };
  return { mutate };
};

/**
 * Permanently hides the compromised password banner for the current user.
 */
export const useDismissPwnedBannerMutation = () =>
  useMutation({
    mutationFn: async () => {
      await apiFetchVendorV1("/user/pwned-banner/dismiss", {
        method: "POST"
      });
    }
  });

/**
 * A react-query custom hook that retrieves the current user's team feature values.
 */
export const useFeatureValues = () => {
  const { isAuthenticated } = useAppState();

  const fetchFeatures = async (): Promise<FeaturesResponse> => {
    const response = await apiFetchVendorV1("/user/features", {
      enableErrorHandling: false
    });
    if (!response.ok) {
      throw new Error(`Unable to load team feature settings (${response.status})`);
    }
    return response.json();
  };

  const { data, error, isFetching, isLoading, isPlaceholderData, refetch } = useQuery({
    queryKey: ["features"],
    queryFn: fetchFeatures,
    enabled: isAuthenticated,
    placeholderData: { features: [], futureFeatures: [] },
    select: data => {
      const features: Partial<Record<keyof typeof adminFeatureFlags, string>> = (
        data?.features || []
      ).reduce((acc, { Key, Value }: FeatureValue) => {
        return { ...acc, [Key]: Value };
      }, {});
      const futureFeatures: Partial<Record<keyof typeof futureFeatureFlags, boolean>> =
        (data?.futureFeatures || []).reduce((acc, feature) => {
          return { ...acc, [feature]: true };
        }, {});
      return { features, futureFeatures };
    }
  });
  return {
    ...data,
    featuresLoading: isLoading || isPlaceholderData || isFetching,
    featuresError: error,
    refetchFeatures: refetch
  };
};

export const useKotsReleaseQuery = (appId: string, releaseSequence: string) => {
  const shouldFetch = !!releaseSequence && !isNaN(Number(releaseSequence));

  const query = useQuery<{ release: KotsRelease }, Error, KotsRelease>({
    queryKey: ["getKotsRelease", releaseSequence, appId],
    queryFn: async ({ queryKey }): Promise<{ release: KotsRelease }> => {
      const releaseSequence = Number(queryKey[1]);
      const appId = queryKey[2];
      const path = `/app/${appId}/release/${releaseSequence}`;
      return (await apiFetchVendor(path)).json();
    },
    select: data => data?.release,
    enabled: shouldFetch
  });
  if (!shouldFetch) {
    return {
      data: null,
      error:
        // check if it's not a valid release sequence && it's also not an empty string (not a draft)
        isNaN(Number(releaseSequence)) && releaseSequence
          ? new Error(
              "Release not found. It could be that you don't have access or that you've tried to access a page that doesn't exist. Please try again or contact your team administrator to validate your privileges."
            )
          : null,
      isLoading: false,
      refetch: () => {}
    };
  }

  return query;
};

export const useImageReleaseSearchQuery = (
  appId: string,
  image: string,
  digest?: string | null,
  enabled = true,
  currentPage = 0,
  pageSize = 20
) => {
  const normalizedDigest = digest?.trim() || "";
  const shouldFetch = Boolean(appId && image && enabled);

  const query = useQuery<ImageReleaseSearchResponse, Error>({
    queryKey: ["imageReleaseSearch", appId, image, normalizedDigest, currentPage, pageSize],
    queryFn: async ({ queryKey }) => {
      const appId = queryKey[1] as string;
      const image = queryKey[2] as string;
      const digest = queryKey[3] as string;
      const currentPage = queryKey[4] as number;
      const pageSize = queryKey[5] as number;
      const params = new URLSearchParams({
        image,
        currentPage: String(currentPage),
        pageSize: String(pageSize),
      });
      if (digest) {
        params.set("digest", digest);
      }
      const path = `/app/${appId}/releases/search?${params.toString()}`;
      return (await apiFetchVendor(path)).json();
    },
    enabled: shouldFetch,
    retry: false,
  });

  if (!shouldFetch) {
    return {
      data: null,
      error: null,
      isLoading: false,
      refetch: () => {},
    };
  }

  return query;
};

export const useReleaseExposure = (appId: string, releaseSequence: string) => {
  const shouldFetch = !!appId && !!releaseSequence && !isNaN(Number(releaseSequence));

  const query = useQuery<ReleaseExposure, Error>({
    queryKey: ["getReleaseExposure", appId, releaseSequence],
    queryFn: async ({ queryKey }): Promise<ReleaseExposure> => {
      const appId = queryKey[1];
      const releaseSequence = Number(queryKey[2]);
      const path = `/app/${appId}/release/${releaseSequence}/exposure`;
      return (await apiFetchVendor(path)).json();
    },
    enabled: shouldFetch
  });

  if (!shouldFetch) {
    return {
      data: null,
      error: null,
      isLoading: false,
      refetch: () => {}
    };
  }

  return query;
};

export const useKotsReleaseSBOMQuery = (appId: string, channelId: string, channelSequence: number, isEmbeddedCluster?: boolean, architecture = "") => {
  const shouldFetch = !!appId && !!channelId && !isNaN(channelSequence);

  const query = useQuery<SBOMResponse, Error>({
    queryKey: ["getKotsReleaseSBOM", appId, channelId, channelSequence, isEmbeddedCluster, architecture],
    queryFn: async ({ queryKey }): Promise<SBOMResponse> => {
      const appId = queryKey[1] as string;
      const channelId = queryKey[2] as string;
      const channelSequence = queryKey[3] as number;
      const isEmbeddedCluster = queryKey[4] as boolean | undefined;
      const architecture = queryKey[5] as string;
      const params = new URLSearchParams({
        channel_sequence: channelSequence.toString(),
        unified_sbom: "true"
      });
      if (isEmbeddedCluster !== undefined && isEmbeddedCluster !== null) {
        params.append("is_embedded_cluster", isEmbeddedCluster.toString());
      }
      if (architecture) {
        params.append("architecture", architecture);
      }
      const path = `/app/${appId}/channel/${channelId}/securebuild/sbom?${params.toString()}`;
      const response = await apiFetchVendor(path, { acceptedStatuses: [404] });
      if (response.status === 404) {
        return { sboms: {} };
      }
      return response.json();
    },
    enabled: shouldFetch,
    retry: 2
  });

  if (!shouldFetch) {
    return {
      data: null,
      error: null,
      isLoading: false,
      refetch: () => {}
    };
  }

  return query;
};

/**
 * A react-query custom hook that retrieves security scan data for a KOTS release.
 * @param appId - the id of the app
 * @param channelId - the id of the channel
 * @param channelSequence - the channel sequence number
 * @param isEmbeddedCluster - optional boolean indicating if the release has embedded cluster as an available install type. If not provided, defaults to false.
 * @param architecture - optional architecture to scope the release results to.
 * @returns result partial {@link UseQueryResult}
 * @example
 * Basic usage (defaults to is_embedded_cluster=false)
 * const { data } = useKotsReleaseScanQuery(appId, channelId, channelSequence);
 *
 * With embedded cluster enabled
 * const { data } = useKotsReleaseScanQuery(appId, channelId, channelSequence, true);
 *
 * With embedded cluster disabled
 * const { data } = useKotsReleaseScanQuery(appId, channelId, channelSequence, false);
 */
export const useKotsReleaseScanQuery = (
  appId: string,
  channelId: string,
  channelSequence: number,
  isEmbeddedCluster = false,
  architecture = ""
) => {
  const shouldFetch = !!appId && !!channelId && !isNaN(channelSequence);

  const { data, error, isLoading, isFetching, dataUpdatedAt, refetch } = useQuery<ScanResponse, Error>({
    queryKey: ["getKotsReleaseScan", appId, channelId, channelSequence, isEmbeddedCluster, architecture],
    queryFn: async ({ queryKey }): Promise<ScanResponse> => {
      const appId = queryKey[1];
      const channelId = queryKey[2];
      const channelSequence = queryKey[3];
      const isEmbeddedCluster = queryKey[4];
      const architecture = queryKey[5];
      const architectureQuery = architecture ? `&architecture=${encodeURIComponent(String(architecture))}` : "";
      const path = `/app/${appId}/channel/${channelId}/securebuild/scan?channel_sequence=${channelSequence}&is_airgap=false&is_embedded_cluster=${isEmbeddedCluster}${architectureQuery}`;
      return (await apiFetchVendor(path)).json();
    },
    enabled: shouldFetch,
    retry: 2
  });

  return { data, error, isLoading, isFetching, dataUpdatedAt, refetch };
};

/**
 * A react-query custom hook that retrieves security scan data for a customer instance.
 * @param appId - the id of the app
 * @param customerId - the id of the customer
 * @param instanceId - the id of the instance
 * @returns result partial {@link UseQueryResult}
 * @example
 * const { data } = useInstanceScanQuery(appId, customerId, instanceId);
 */
export const useInstanceScanQuery = (
  appId: string,
  customerId: string,
  instanceId: string,
  clusterId?: string,
  enabled = true,
) => {
  const shouldFetch = enabled && !!appId && !!customerId && !!instanceId;

  const { data, error, isLoading, refetch } = useQuery<ScanResponse, Error>({
    queryKey: ["getInstanceScan", appId, customerId, instanceId, clusterId ?? ""],
    queryFn: async ({ queryKey }): Promise<ScanResponse> => {
      const appId = queryKey[1];
      const customerId = queryKey[2];
      const instanceId = queryKey[3];
      const queryParams = new URLSearchParams();
      if (clusterId) {
        queryParams.set("cluster_id", clusterId);
      }
      const path = `/app/${appId}/customer/${customerId}/instance/${instanceId}/scan${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      return (await apiFetchVendor(path)).json();
    },
    enabled: shouldFetch,
    retry: 2
  });

  return { data, error, isLoading, refetch };
};

export interface CustomerImpactQueryParams {
  appId: string;
  // Customer search term, resolved through the same engine as the customers
  // page's POST /customers/search: a bare term is a fuzzy/prefix name match,
  // and its key:value syntax (name:, type:, channels.name:, customId:,
  // email:) works too.
  search?: string;
  // Severities to filter by (OR semantics: a customer is kept when its selected
  // worst instance has >=1 finding in any selected severity). Empty means no
  // severity filtering.
  severities?: string[];
  // Include unfixable CVEs in all counts.
  includeUnfixable?: boolean;
  // Include the unfiltered fleet summary and derive the returned customer page
  // from that same authorized population. The API rejects this when search or
  // severity filters are also supplied.
  includeSnapshot?: boolean;
  // customerName | criticalCVEs | totalCVEs | instanceCount
  sortBy?: string;
  pageSize?: number;
  // 0-based page index.
  currentPage?: number;
  enabled?: boolean;
}

/**
 * A react-query custom hook backing the Security Center "By Customer" tab. It
 * consumes the customer-impact API, which filters/sorts/paginates server-side —
 * every param below maps to a query-string arg. `totalCustomers` in the
 * response reflects all matches, so it feeds the Pager directly.
 *
 * @example
 * const { data, isLoading } = useCustomerImpactCustomers({ appId, search, severities, sortBy, pageSize, currentPage });
 */
export const useCustomerImpactCustomers = ({
  appId,
  search = "",
  severities = [],
  includeUnfixable = false,
  includeSnapshot = false,
  sortBy = "criticalCVEs",
  pageSize = 20,
  currentPage = 0,
  enabled = true
}: CustomerImpactQueryParams) => {
  // Sort so the query key is order-independent — ["critical","high"] and
  // ["high","critical"] are the same filter and shouldn't refetch.
  const stableSeverities = [...severities].sort();
  const shouldFetch = enabled && !!appId;

  return useQuery<CustomerImpactResponse, Error>({
    queryKey: [
      "securityCustomerImpact",
      appId,
      { search, severities: stableSeverities, includeUnfixable, includeSnapshot, sortBy, pageSize, currentPage }
    ] as const,
    queryFn: async ({ queryKey }): Promise<CustomerImpactResponse> => {
      const appId = queryKey[1] as string;
      const { search, severities, includeUnfixable, includeSnapshot, sortBy, pageSize, currentPage } = queryKey[2] as {
        search: string;
        severities: string[];
        includeUnfixable: boolean;
        includeSnapshot: boolean;
        sortBy: string;
        pageSize: number;
        currentPage: number;
      };
      const params = new URLSearchParams();
      params.set("pageSize", String(pageSize));
      params.set("currentPage", String(currentPage));
      params.set("sortBy", sortBy);
      if (search) {
        params.set("search", search);
      }
      if (includeUnfixable) {
        params.set("includeUnfixable", "true");
      }
      if (includeSnapshot) {
        params.set("includeSnapshot", "true");
      }
      // Repeatable param — the API accepts one ?severity= per value.
      severities.forEach(severity => params.append("severity", severity));
      const path = `/app/${appId}/security/customer-impact/customers?${params.toString()}`;
      const response = await apiFetchVendor(path);
      if (!response.ok) {
        throw new Error(`Failed to load customer impact: ${response.status}`);
      }
      return response.json();
    },
    enabled: shouldFetch,
    keepPreviousData: true,
    retry: 2
  });
};

interface CustomerImpactCVEsQueryParams {
  appId: string;
  search?: string;
  severity?: string;
  includeUnfixable?: boolean;
  pageSize?: number;
  currentPage?: number;
  enabled?: boolean;
}

export const useCustomerImpactCVEs = ({
  appId,
  search = "",
  severity = "",
  includeUnfixable = false,
  pageSize = 20,
  currentPage = 0,
  enabled = true
}: CustomerImpactCVEsQueryParams) =>
  useQuery<CustomerImpactCVEsResponse, Error>({
    queryKey: ["securityCustomerImpactCVEs", appId, { search, severity, includeUnfixable, pageSize, currentPage }] as const,
    queryFn: async ({ queryKey }) => {
      const appId = queryKey[1] as string;
      const options = queryKey[2] as {
        search: string;
        severity: string;
        includeUnfixable: boolean;
        pageSize: number;
        currentPage: number;
      };
      const params = new URLSearchParams({
        pageSize: String(options.pageSize),
        currentPage: String(options.currentPage)
      });
      if (options.search) params.set("search", options.search);
      if (options.severity) params.set("severity", options.severity);
      if (options.includeUnfixable) params.set("includeUnfixable", "true");
      const response = await apiFetchVendor(`/app/${appId}/security/customer-impact/cves?${params}`);
      if (!response.ok) throw new Error(`Failed to load CVE customer impact: ${response.status}`);
      return response.json();
    },
    enabled: enabled && !!appId,
    retry: 2
  });

interface CustomerImpactCVEDetailQueryParams {
  appId: string;
  cveId: string;
  includeUnfixable?: boolean;
  enabled?: boolean;
}

export const useCustomerImpactCVEDetail = ({
  appId,
  cveId,
  includeUnfixable = false,
  enabled = true
}: CustomerImpactCVEDetailQueryParams) =>
  useQuery<CustomerImpactCVEDetail, Error>({
    queryKey: ["securityCustomerImpactCVE", appId, cveId, { includeUnfixable }] as const,
    queryFn: async ({ queryKey }) => {
      const appId = queryKey[1] as string;
      const cveId = queryKey[2] as string;
      const { includeUnfixable } = queryKey[3] as { includeUnfixable: boolean };
      const params = new URLSearchParams();
      if (includeUnfixable) params.set("includeUnfixable", "true");
      const suffix = params.size ? `?${params}` : "";
      const response = await apiFetchVendor(`/app/${appId}/security/customer-impact/cves/${encodeURIComponent(cveId)}${suffix}`);
      if (!response.ok) throw new Error(`Failed to load impact for ${cveId}: ${response.status}`);
      return response.json();
    },
    enabled: enabled && !!appId && !!cveId,
    retry: 2
  });

interface CustomerImpactCVEReleaseCustomersQueryParams {
  appId: string;
  cveId: string;
  channelId: string;
  channelSequence: number;
  includeUnfixable?: boolean;
  pageSize?: number;
  enabled?: boolean;
}

export const useCustomerImpactCVEReleaseCustomers = ({
  appId,
  cveId,
  channelId,
  channelSequence,
  includeUnfixable = false,
  pageSize = 20,
  enabled = true
}: CustomerImpactCVEReleaseCustomersQueryParams) =>
  useInfiniteQuery<CustomerImpactCVEReleaseCustomersResponse, Error>({
    queryKey: [
      "securityCustomerImpactCVE",
      appId,
      cveId,
      "releaseCustomers",
      channelId,
      channelSequence,
      { includeUnfixable, pageSize }
    ] as const,
    queryFn: async ({ pageParam = 0 }) => {
      const currentPage = typeof pageParam === "number" ? pageParam : 0;
      const params = new URLSearchParams({
        pageSize: String(pageSize),
        currentPage: String(currentPage)
      });
      if (includeUnfixable) params.set("includeUnfixable", "true");
      const response = await apiFetchVendor(
        `/app/${appId}/security/customer-impact/cves/${encodeURIComponent(cveId)}` +
        `/releases/${encodeURIComponent(channelId)}/${channelSequence}/customers?${params}`
      );
      if (!response.ok) {
        throw new Error(`Failed to load affected customers for ${cveId} release ${channelSequence}: ${response.status}`);
      }
      return response.json();
    },
    getNextPageParam: (lastPage, pages) => {
      const loadedCustomers = pages.reduce((total, page) => total + page.customers.length, 0);
      return lastPage.customers.length > 0 && loadedCustomers < lastPage.totalCustomers
        ? pages.length
        : undefined;
    },
    enabled: enabled && !!appId && !!cveId && !!channelId && channelSequence > 0,
    retry: 2
  });

/**
 * A react-query custom hook that retrieves the releases for an app.
 * @param appId - the id of the app
 * @param startIndex - the index to start at for pagination
 * @param pageCount - how many releases to fetch for pagination
 * @returns result partial {@link UseQueryResult}
 * @example
 * const { data } = useKotsReleasesQuery(appId);
 */
export const useKotsReleasesQuery = (appId: string, startIndex = 0, pageCount = 20, includeSecuritySummary = false) => {
  const dispatch = useDispatch();
  const { refetch } = useReduxQuery({
    queryKey: ["kotsReleases", appId, startIndex, pageCount, includeSecuritySummary] as const,
    queryFn: ({ queryKey }) => {
      return dispatch(getKotsReleaseList(queryKey[1], queryKey[2], queryKey[3], queryKey[4]));
    },
    enabled: !!appId
  });
  const data: KotsReleasesResponse = {
    releases: useSelector<any, KotsReleasesResponse["releases"]>(
      (state: any) => state.kots.releases.kotsReleases
    ),
    approximateTotalCount: useSelector<
      any,
      KotsReleasesResponse["approximateTotalCount"]
    >((state: any) => state.kots.releases.approximateTotalCount)
  };
  const isLoading = useSelector<any, DataLoading>(
    (state: any) => state.ui.main.loading
  ).kotsReleasesLoading;
  return { data, isLoading, refetch };
};

/**
 * A react-query custom hook that retrieves the active releases for an app.
 * @param appId - the id of the app
 * @param includeSecuritySummary - whether to include security summary information for releases
 * @returns result partial {@link UseQueryResult}
 * @example
 * const { data } = useActiveKotsReleasesQuery(appId);
 */
export const useActiveKotsReleasesQuery = (appId: string, includeSecuritySummary = false) => {
  const dispatch = useDispatch();
  const { refetch } = useReduxQuery({
    queryKey: ["activeKotsReleases", appId, includeSecuritySummary] as const,
    queryFn: ({ queryKey }) => dispatch(getActiveKotsReleases(queryKey[1], queryKey[2])),
    enabled: Boolean(appId)
  });
  const data = useSelector<any, KotsReleasesResponse["releases"]>(
    (state: any) => state.kots.releases.activeKotsReleases
  );
  const isLoading = useSelector<any, DataLoading>(
    (state: any) => state.ui.main.loading
  ).activeKotsReleasesLoading;
  return { data, isLoading, refetch };
};

export const useLatestReplicatedSDKQuery = () => {
  return useQuery({
    queryKey: ["latestReplicatedSDKVersion"],
    queryFn: async (): Promise<{ version: string }> => {
      const path = `/replicated-sdk-version?strict=true`;
      return (await apiFetchVendor(path)).json();
    }
  });
};

interface CustomerSearchQueryParams {
  appId?: string;
  query?: string;
  pageSize?: number;
  page?: number;
  includeActive?: boolean;
  includeInactive?: boolean;
  includeArchived?: boolean;
  includeLicenseTypes?: string[];
  channelNames: (string | null)[];
  sortField?: string;
  sortDirection?: string;
  adoptionRateFilter?: string;
}

/**
 * A react-query custom hook that retrieves a list of customers for a given search query.
 *
 * @param config - a {@link CustomerSearchQueryParams} object, configures the query
 * @param config.appId - the id of the app
 * @param config.query - the search query
 * @param config.pageSize - the number of results to return per page
 * @param config.page - the page number to return
 * @param config.includeActive - whether to include active customers
 * @param config.includeInactive - whether to include inactive customers
 * @param config.includeArchived - whether to include archived customers
 * @param config.includeLicenseTypes - the license types to include
 * @param config.channelNames - the channel names to include
 * @param config.sortField - the field to sort by
 * @param config.sortDirection - the direction to sort by
 * @returns result {@link UseQueryResult}
 */
export const useCustomerSearchQuery = ({
  appId: appIdParam,
  query: queryParam = "",
  pageSize: pageSizeParam = 20,
  page: pageParam = 0,
  includeActive: includeActiveParam = true,
  includeInactive: includeInactiveParam = true,
  includeArchived: includeArchivedParam = false,
  adoptionRateFilter: adoptionRateFilterParam,
  includeLicenseTypes: includeLicenseTypesParam = [],
  channelNames: channelNamesParam,
  sortField: sortFieldParam = "createdAt",
  sortDirection: sortDirectionParam = "desc"
}: CustomerSearchQueryParams) => {
  const { isAuthenticated } = useAppState();
  const [sortParam] = useQueryParam("sort", "");
  // Convert to objects to ensure that the query key is stable,
  // and filter for valid values
  const stableLicenseTypes = includeLicenseTypesParam.flatMap(value => {
    if (!value) {
      return [];
    }
    if (!["dev", "trial", "paid", "community"].includes(value)) {
      console.error(`Search: Invalid license type "${value}", ignoring`);
      return [];
    }
    return { value };
  });
  const stableChannelNames = channelNamesParam.flatMap(value => {
    if (!value) {
      return [];
    }
    return { value };
  });
  const validSortDirection = sortDirectionParam === "asc" ? "asc" : "desc";
  return useQuery({
    queryKey: [
      "customersBySearch",
      appIdParam,
      { query: queryParam, pageSize: pageSizeParam, page: pageParam },
      {
        active: includeActiveParam,
        inactive: includeInactiveParam,
        archived: includeArchivedParam,
        licenseTypes: stableLicenseTypes,
        channelNames: stableChannelNames,
        adoptionRateFilter: adoptionRateFilterParam
      },
      {
        sortField: sortFieldParam,
        sortDirection: validSortDirection
      }
    ] as const,
    queryFn: async ({ queryKey }): Promise<CustomerSearchResponse> => {
      const appId = queryKey[1];
      const { query, pageSize, page } = queryKey[2];
      const {
        active,
        inactive,
        archived,
        licenseTypes,
        channelNames,
        adoptionRateFilter
      } = queryKey[3];
      const { sortField, sortDirection } = queryKey[4];
      const isIncluded = (value: string, arr: { value: string }[]) => {
        if (arr.length === 0) {
          return true;
        }
        return arr.some(({ value: arrValue }) => value === arrValue);
      };
      const response = await apiFetchVendor("/customers/search", {
        method: "POST",
        body: JSON.stringify({
          app_id: appId,
          query,
          offset: page > 0 ? pageSize * page : 0,
          page_size: pageSize,
          channel_names: channelNames.map(({ value }) => value),
          sort_field: sortField,
          sort_direction: sortDirection,
          include_dev: isIncluded("dev", licenseTypes),
          include_trial: isIncluded("trial", licenseTypes),
          include_paid: isIncluded("paid", licenseTypes),
          include_community: isIncluded("community", licenseTypes),
          include_active: active || !inactive,
          include_inactive: inactive || !active,
          include_archived: archived,
          instance_preview: true,
          ...(adoptionRateFilter ? { adoption_rate_filter: adoptionRateFilter } : {})
        })
      });
      return response?.json();
    },
    cacheTime: 0,
    keepPreviousData: true,
    // Only enable the query if the user is authenticated and all required params are present
    // We check for sortParam here because we need to wait for the sort query param to be set after we fetch the user's customer table settings
    // We check for appIdParam to ensure we don't execute the query with an invalid/empty appId
    enabled: isAuthenticated && !!appIdParam && !!sortParam
  });
};

interface UserPublicKey {
  user_id: string;
  public_key: string;
  fingerprint: string;
  is_synced: boolean;
}

interface UserPublicKeysResponse {
  public_keys: UserPublicKey[];
}

/**
 * A react-query custom hook that retrieves the authenticated user's public keys.
 * @returns result partial {@link UseQueryResult}
 * @example
 * const { data, isLoading } = useUserPublicKeysQuery();
 */
export const useUserPublicKeysQuery = () => {
  const { isAuthenticated } = useAppState();

  return useQuery<UserPublicKeysResponse, Error>({
    queryKey: ["userPublicKeys"],
    queryFn: async (): Promise<UserPublicKeysResponse> => {
      const path = `/cmx/user/public-keys`;
      return (await apiFetchVendor(path)).json();
    },
    enabled: isAuthenticated,
  });
};

interface AddUserPublicKeyRequest {
  user_id: string;
  public_key: string;
}

interface AddUserPublicKeyResponse {
  public_key: UserPublicKey;
}

/**
 * A react-query custom hook for adding a new SSH public key for the authenticated user.
 * @returns mutation object with mutate function and loading/error states
 * @example
 * const { mutate, isLoading, error } = useAddUserPublicKeyMutation();
 * mutate({ user_id: 'myusername', public_key: 'ssh-rsa AAAA...' });
 */
export const useAddUserPublicKeyMutation = () => {
  return useMutation<AddUserPublicKeyResponse, Error, AddUserPublicKeyRequest>({
    mutationFn: async (request: AddUserPublicKeyRequest) => {
      const path = `/cmx/user/public-keys`;
      const response = await apiFetchVendor(path, {
        method: "POST",
        body: JSON.stringify(request)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add public key");
      }
      return response.json();
    }
  });
};

interface DeleteUserPublicKeyRequest {
  user_id: string;
  fingerprint: string;
}

interface DeleteUserPublicKeyResponse {
  success: boolean;
}

/**
 * A react-query custom hook for deleting an SSH public key for the authenticated user.
 * @returns mutation object with mutate function and loading/error states
 * @example
 * const { mutate, isLoading, error } = useDeleteUserPublicKeyMutation();
 * mutate({ user_id: 'myusername', fingerprint: 'SHA256:...' });
 */
export const useDeleteUserPublicKeyMutation = () => {
  return useMutation<DeleteUserPublicKeyResponse, Error, DeleteUserPublicKeyRequest>({
    mutationFn: async (request: DeleteUserPublicKeyRequest) => {
      const path = `/cmx/user/public-keys/${encodeURIComponent(request.user_id)}/${encodeURIComponent(request.fingerprint)}`;
      const response = await apiFetchVendor(path, {
        method: "DELETE"
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete public key");
      }
      return response.json();
    }
  });
};
